import { GuestPreferenceStatModel } from 'api-hooks/guest';
import { Option, Value } from 'baseui/select';
import { PreferenceGroupSubTypeEnum } from 'common/constants/enum';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useGetMultiplePreferenceColumn from 'hooks/use-get-multiple-preference-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  ActivitySaleGuestItemFormType,
  ActivitySaleFormType,
} from '../form-type';
import { addNewGuestItem } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function tagTransformer(item: GuestPreferenceStatModel): Option {
  return {
    id: item.id,
    label: `${item.name} ${item.count > 1 ? `(X${item.count})` : ''}`,
  };
}

function useGetGuestTableList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();
  const multiplePreferenceColumn = useGetMultiplePreferenceColumn({
    subType: PreferenceGroupSubTypeEnum.Activity,
  });
  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:guest_preferences'),
      accessorKey: 'guestPreferences',
      minSize: 300,
      dataType: ColumnDataType.tags,
      tagKeyName: 'label',
      maxTags: 2,
    },
    {
      header: t('guest:booking_preferences'),
      accessorKey: 'preferences',
      minSize: 250,
      dataType: ColumnDataType.selectMulti,
      selectProps: multiplePreferenceColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function ActivityGuestTable(props: Props) {
  const { editable } = useFormState();
  const name = `activitySales.${props.parentIndex}.activitySaleGuests` as const;

  const { control, trigger, getValues, clearErrors, setValue } =
    useFormContext<ActivitySaleFormType>();

  const fields = getValues(name);

  const { append, remove, update } = useFieldArray({
    name,
    control,
    keyName: '__customId',
  });

  const columns = useGetGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof ActivitySaleGuestItemFormType;
      const currentRowIndex = action.rowIndex as number;
      const value = action.value;
      const extra = action.extra as Value;
      const guests = getValues(name);
      const guest = guests[currentRowIndex];
      const isGroupedPrice = getValues(
        `activitySales.${props.parentIndex}.isGroupedPrice`,
      );

      const otherVal: () => Partial<ActivitySaleGuestItemFormType> = () => {
        if (action.columnId === 'guestId') {
          const allPreference = action.extra?.[0]
            ? [
                ...action.extra[0].extra.preferences,
                ...action.extra[0].extra.preferenceStats,
              ]
            : [];

          const guestName = extra?.[0]?.label as string;

          return {
            guestPreferences: (allPreference || []).map((item) =>
              tagTransformer(item),
            ),
            guestName,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });

        if (isGroupedPrice && columnId === 'guestId') {
          guests.forEach((_, index) => {
            if (index === 0) return;
            setValue(
              `${name}.${index}.activitySaleGuestDetails`,
              guests[0].activitySaleGuestDetails,
            );
          });
        }
        clearErrors(name);
      }
    },
    [
      append,
      clearErrors,
      getValues,
      name,
      props.parentIndex,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        name={name}
        dispatch={dispatch}
        disabled={!editable}
        uniqueRowKey="customId"
      />
    </div>
  );
}
